/**
 * exam-bank/paper-view — 把一份「卷」渲染成整張段考卷的排版(不是一問一答)。
 *
 * 老闆回饋(2026-06-07):真題練習要像真實段考卷 —— 一次看到整張、分大題
 * (一、二、三…)、阿拉伯題號、選項/作答格都印在版面上,而不是一次只給一題。
 * 內容仍是 AI 生的類似題型,重點是「版面要像考卷」。
 *
 * 視覺 = 學術風格設計系統(boss 2026-06-08;見 wiki she-ji-xi-tong / page-470f59c0)。
 * 外框套 `pageShell`(紙底頁殼 + serif 標題列 + ✕ 離開);考卷本體維持「像考卷」
 * (卷頭 + 大題分段 + 連續題號 + 作答格 + toggle 解答 + 列印),但改用學術紙感
 * `--ef-*` token(紙白卷面、墨水文字、學術綠大題條、克制的紅筆訂正),不再硬寫
 * `#fff` / `#111`。互動只兩顆學術按鈕:顯示解答(primary)、列印(secondary)。
 *
 * 學術鐵則(boss 2026-05-23):純文字、極簡,無音效、無 mascot、無彩光。
 * 學習/遊戲分離鐵則:純學習側,絕不碰 commanderXp / factionXP / 戰鬥數值。
 */

import { mountEscapeButton } from '../escape-button';
import { ensureAcademicTheme } from '../ui/theme';
import { academicButton } from '../ui/components';
import type { ExamPaperEntry, NormQ } from './loader';

/** 題型 → 大題中文標題。決定該卷分幾個大題、各題排法。 */
const KIND_SECTION_LABEL: Record<string, string> = {
  tf: '是非題',
  mcq: '選擇題',
  'image-mcq': '選擇題(看圖)',
  fill: '填充題',
  'word-bank-fill': '填充題(選詞填空)',
  compute: '計算題',
  inverse: '計算題',
  word: '應用題',
  'zhuyin-word': '注音造詞',
  reading: '閱讀測驗',
  'make-sentence': '造句',
  check: '勾選題',
  match: '配合題',
  'fill-grid': '填表題',
};

/** 大題顯示順序(沒列到的 kind 接在後面,維持卷內出現順序)。 */
const SECTION_ORDER: string[] = [
  '是非題',
  '選擇題',
  '選擇題(看圖)',
  '填充題',
  '填充題(選詞填空)',
  '計算題',
  '應用題',
  '注音造詞',
  '造句',
  '勾選題',
  '配合題',
  '填表題',
  '閱讀測驗',
];

/** 國字大題編號。 */
const CJK_NUM = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二'];

function sectionLabel(kind: string): string {
  return KIND_SECTION_LABEL[kind] ?? '其他';
}

/**
 * 渲染一整張考卷版面到 `container`(可捲動 DOM,一次呈現整張)。
 *
 * - 卷頭:標題 + 班級/姓名/日期/得分 欄(像考卷)。
 * - 依題型分大題:一、是非題 / 二、選擇題 / …(只列該卷實際出現的 kind)。
 * - 每題:阿拉伯題號 + prompt + 對應作答格(選項 / (  ) / 底線 / 作答區)。
 * - 上方按鈕列:顯示解答(toggle)、列印(window.print)、返回(escape 由 onBack)。
 *
 * @param onBack 返回卷清單。由本函式 mount 的 escape 按鈕(✕ 離開)觸發。
 */
export function renderExamPaper(
  container: HTMLElement,
  paper: ExamPaperEntry,
  onBack: () => void,
): void {
  container.innerHTML = '';
  ensureAcademicTheme();

  // escape 按鈕(✕ 離開)→ 回卷清單。
  const unmountEscape = mountEscapeButton(() => {
    unmountEscape();
    onBack();
  });

  // 學術紙底「書桌」:考卷像一張真的講義紙(--ef-paper-bright)鋪在紙色桌面上。
  const desk = document.createElement('div');
  desk.className = 'exam-paper-desk ef-paper-desk';
  desk.style.cssText = DESK_STYLE;

  const root = document.createElement('div');
  root.className = 'exam-paper ef-paper-sheet';
  // 考卷語彙保留(襯線、A4 寬度上限、置中作答),底色 / 文字改學術 token。
  root.style.cssText = SHEET_STYLE;

  // ── 按鈕列(學術按鈕:顯示解答 = primary、列印 = secondary)──────────────
  const toolbar = document.createElement('div');
  toolbar.className = 'exam-paper-toolbar';
  toolbar.style.cssText = 'display:flex;gap:12px;margin:0 0 22px;flex-wrap:wrap;';

  let answersShown = false;
  const toggleBtn = academicButton({ label: '✏️ 顯示解答', variant: 'primary', onClick: () => {} });
  toggleBtn.classList.add('exam-paper-toggle-answers');
  toggleBtn.addEventListener('click', () => {
    answersShown = !answersShown;
    root.classList.toggle('answers-shown', answersShown);
    toggleBtn.textContent = answersShown ? '✏️ 隱藏解答' : '✏️ 顯示解答';
    root.querySelectorAll<HTMLElement>('.exam-paper-answer').forEach((el) => {
      el.style.display = answersShown ? '' : 'none';
    });
  });

  const printBtn = academicButton({ label: '🖨️ 列印', variant: 'secondary', onClick: () => printPaper(paper) });
  printBtn.classList.add('exam-paper-print');

  toolbar.append(toggleBtn, printBtn);
  root.appendChild(toolbar);

  // ── 卷頭(像考卷:標題 + 班級/姓名/日期/得分;改學術墨線)──────────────────
  const header = document.createElement('div');
  header.className = 'exam-paper-header';
  header.style.cssText = 'border-bottom:2px solid var(--ef-ink);padding-bottom:12px;margin-bottom:20px;';

  const titleEl = document.createElement('h1');
  titleEl.className = 'exam-paper-title';
  titleEl.style.cssText =
    'font-family:var(--ef-serif);font-size:23px;font-weight:700;letter-spacing:1px;margin:0 0 10px;text-align:center;color:var(--ef-ink);';
  titleEl.textContent = paper.title;
  header.appendChild(titleEl);

  const meta = document.createElement('div');
  meta.className = 'exam-paper-meta';
  meta.style.cssText =
    'display:flex;flex-wrap:wrap;gap:18px;font-size:14px;color:var(--ef-ink-soft);';
  meta.innerHTML = `
    <span>班級:${blank('3em')}</span>
    <span>姓名:${blank('5em')}</span>
    <span>日期:${blank('5em')}</span>
    <span>得分:${blank('3em')}</span>
  `;
  header.appendChild(meta);
  root.appendChild(header);

  // ── 分大題 ──────────────────────────────────────────────────────────────
  const sections = groupBySection(paper.questions);
  if (sections.length === 0) {
    const empty = document.createElement('div');
    empty.style.cssText = 'color:var(--ef-sub);';
    empty.textContent = '這份卷沒有題目。';
    root.appendChild(empty);
  }

  let qNum = 0; // 題號跨大題連續編號(像真考卷)。
  sections.forEach((section, sIdx) => {
    const sectionEl = document.createElement('div');
    sectionEl.className = 'exam-paper-section';
    sectionEl.style.cssText = 'margin:0 0 24px;';

    const heading = document.createElement('h2');
    heading.className = 'exam-paper-section-title';
    heading.style.cssText =
      'font-family:var(--ef-serif);font-size:17px;font-weight:700;letter-spacing:0.5px;margin:0 0 12px;border-left:5px solid var(--ef-green);padding-left:10px;color:var(--ef-ink);';
    heading.textContent = `${CJK_NUM[sIdx] ?? sIdx + 1}、${section.label}`;
    sectionEl.appendChild(heading);

    for (const q of section.questions) {
      qNum += 1;
      sectionEl.appendChild(renderQuestion(q, qNum));
    }
    root.appendChild(sectionEl);
  });

  desk.appendChild(root);
  container.appendChild(desk);

  // 初始隱藏解答。
  root.querySelectorAll<HTMLElement>('.exam-paper-answer').forEach((el) => {
    el.style.display = 'none';
  });
}

/**
 * 考卷紙面共用 style —— 學術紙白卷面(--ef-paper-bright)+ 墨水文字 + 細紙線框 +
 * 極淺陰影,鋪在頁面紙底上像一張真的講義。襯線、A4 寬度上限、置中作答都保留。
 */
const SHEET_STYLE =
  'font-family:var(--ef-serif);color:var(--ef-ink-soft);max-width:820px;margin:0 auto;' +
  'padding:28px 30px 64px;background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
  'border-radius:var(--ef-radius);box-shadow:var(--ef-shadow-card);line-height:1.7;';

/** 紙底書桌:頁面紙色背景(radial)+ 上下留白,讓白卷面像鋪在講義紙上。 */
const DESK_STYLE =
  'box-sizing:border-box;width:100%;min-height:100%;padding:28px 20px 48px;' +
  'background:radial-gradient(120% 80% at 50% 0%, #faf8f1 0%, var(--ef-paper) 60%);';

// ── 分組 ────────────────────────────────────────────────────────────────────

interface Section {
  label: string;
  questions: NormQ[];
}

/**
 * 依題型分大題。同 label 的 kind 併在同一大題(如 compute/inverse 都算計算題);
 * 大題順序照 SECTION_ORDER,沒列到的接在後面(維持出現順序)。
 */
function groupBySection(questions: NormQ[]): Section[] {
  const byLabel = new Map<string, NormQ[]>();
  const firstSeen: string[] = [];
  for (const q of questions) {
    const label = sectionLabel(q.kind);
    if (!byLabel.has(label)) {
      byLabel.set(label, []);
      firstSeen.push(label);
    }
    byLabel.get(label)!.push(q);
  }

  const ordered: string[] = [];
  for (const label of SECTION_ORDER) {
    if (byLabel.has(label)) ordered.push(label);
  }
  for (const label of firstSeen) {
    if (!ordered.includes(label)) ordered.push(label);
  }

  return ordered.map((label) => ({ label, questions: byLabel.get(label)! }));
}

// ── 單題渲染 ─────────────────────────────────────────────────────────────────

function renderQuestion(q: NormQ, num: number): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'exam-paper-q';
  wrap.style.cssText = 'margin:0 0 18px;';

  // reading 題:先印短文,再印子題。
  if (q.kind === 'reading') {
    return renderReading(q, num);
  }

  const promptEl = document.createElement('div');
  promptEl.className = 'exam-paper-prompt';
  promptEl.style.cssText = 'font-size:15px;margin:0 0 8px;white-space:pre-wrap;color:var(--ef-ink);';
  promptEl.textContent = `${num}. ${blankResolve(q)}`;
  wrap.appendChild(promptEl);

  appendAnswerArea(wrap, q);
  appendAnswerKey(wrap, q);
  return wrap;
}

/** prompt 文字(reading 以外都直接取 q.prompt)。 */
function blankResolve(q: NormQ): string {
  return q.prompt;
}

/** 依 kind 印對應的「作答格」。 */
function appendAnswerArea(wrap: HTMLElement, q: NormQ): void {
  switch (q.kind) {
    case 'mcq':
    case 'image-mcq': {
      if (q.options && q.options.length > 0) {
        const opts = document.createElement('div');
        opts.className = 'exam-paper-options';
        opts.style.cssText = 'display:flex;flex-wrap:wrap;gap:14px;font-size:14px;margin:0 0 6px;';
        q.options.forEach((opt, idx) => {
          const span = document.createElement('span');
          span.className = 'exam-paper-option';
          span.style.cssText = 'padding:2px 4px;color:var(--ef-ink);';
          span.textContent = `(${String.fromCharCode(65 + idx)}) ${opt}`;
          opts.appendChild(span);
        });
        wrap.appendChild(opts);
      }
      appendParenBox(wrap);
      break;
    }
    case 'tf': {
      // (  ) 填 ○/✕
      appendParenBox(wrap);
      break;
    }
    case 'fill':
    case 'word-bank-fill':
    case 'compute':
    case 'inverse':
    case 'zhuyin-word': {
      appendUnderline(wrap);
      break;
    }
    case 'word': {
      // 應用題:留算式 / 作答空間。
      appendWorkBox(wrap);
      break;
    }
    case 'check':
    case 'match':
    case 'fill-grid': {
      // 盡量照原樣排:子項清單 + 作答區。
      appendItemsList(wrap, q);
      appendWorkBox(wrap);
      break;
    }
    default: {
      // make-sentence 及其他開放題:留作答區。
      appendWorkBox(wrap);
    }
  }
}

/** (  ) 作答框(tf / mcq)。 */
function appendParenBox(wrap: HTMLElement): void {
  const box = document.createElement('div');
  box.className = 'exam-paper-paren';
  box.style.cssText = 'font-size:15px;margin:2px 0 0;color:var(--ef-ink);';
  box.textContent = '答(　　)';
  wrap.appendChild(box);
}

/** 底線作答格(fill / compute / zhuyin-word…)。 */
function appendUnderline(wrap: HTMLElement): void {
  const line = document.createElement('div');
  line.className = 'exam-paper-underline';
  line.style.cssText = 'margin:2px 0 0;font-size:15px;color:var(--ef-ink);';
  line.innerHTML = `答:${blank('8em')}`;
  wrap.appendChild(line);
}

/** 計算 / 作答方框(word / 複雜題)。 */
function appendWorkBox(wrap: HTMLElement): void {
  const box = document.createElement('div');
  box.className = 'exam-paper-workbox';
  box.style.cssText =
    'border:1px solid var(--ef-ink);width:100%;max-width:480px;min-height:64px;margin:6px 0 0;border-radius:var(--ef-radius-sm);background:rgba(255,255,255,0.4);';
  wrap.appendChild(box);
}

/** 子項清單(check / match / fill-grid 的 items 原樣列出)。 */
function appendItemsList(wrap: HTMLElement, q: NormQ): void {
  if (!Array.isArray(q.items) || q.items.length === 0) return;
  const list = document.createElement('div');
  list.className = 'exam-paper-items';
  list.style.cssText = 'display:flex;flex-direction:column;gap:4px;margin:4px 0 0;font-size:14px;color:var(--ef-ink-soft);';
  for (const it of q.items) {
    const line = document.createElement('div');
    line.className = 'exam-paper-item';
    line.style.cssText = 'white-space:pre-wrap;';
    line.textContent = describeItem(it);
    list.appendChild(line);
  }
  wrap.appendChild(list);
}

/** reading:先印 passage,再印子題(編號 + 選項 + 作答格)。 */
function renderReading(q: NormQ, num: number): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'exam-paper-q exam-paper-reading';
  wrap.style.cssText = 'margin:0 0 20px;';

  const passage = document.createElement('div');
  passage.className = 'exam-paper-passage';
  passage.style.cssText =
    'background:var(--ef-paper);border:1px solid var(--ef-line);border-radius:var(--ef-radius-sm);padding:12px 14px;margin:0 0 10px;font-size:15px;line-height:1.8;white-space:pre-wrap;color:var(--ef-ink-soft);';
  passage.textContent = q.passage ?? q.prompt;
  wrap.appendChild(passage);

  const subs = Array.isArray(q.items) ? q.items : [];
  if (subs.length === 0) {
    // 沒子題就把整題當一題:留作答區。
    const promptEl = document.createElement('div');
    promptEl.className = 'exam-paper-prompt';
    promptEl.style.cssText = 'font-size:15px;margin:0 0 8px;white-space:pre-wrap;color:var(--ef-ink);';
    promptEl.textContent = `${num}. ${q.prompt}`;
    wrap.appendChild(promptEl);
    appendUnderline(wrap);
  } else {
    subs.forEach((sub, i) => {
      const so = (typeof sub === 'object' && sub !== null ? sub : {}) as Record<string, unknown>;
      const subPrompt =
        typeof so.prompt === 'string' ? so.prompt
        : typeof so.text === 'string' ? so.text
        : String(sub);
      const line = document.createElement('div');
      line.className = 'exam-paper-prompt exam-paper-subq';
      line.style.cssText = 'font-size:15px;margin:6px 0 6px;white-space:pre-wrap;color:var(--ef-ink);';
      line.textContent = `${num}-${i + 1}. ${subPrompt}`;
      wrap.appendChild(line);

      if (Array.isArray(so.options) && so.options.length > 0) {
        const opts = document.createElement('div');
        opts.className = 'exam-paper-options';
        opts.style.cssText = 'display:flex;flex-wrap:wrap;gap:14px;font-size:14px;margin:0 0 4px;';
        so.options.forEach((opt, idx) => {
          const span = document.createElement('span');
          span.className = 'exam-paper-option';
          span.style.cssText = 'padding:2px 4px;color:var(--ef-ink);';
          span.textContent = `(${String.fromCharCode(65 + idx)}) ${String(opt)}`;
          opts.appendChild(span);
        });
        wrap.appendChild(opts);
        appendParenBox(wrap);
      } else {
        appendUnderline(wrap);
      }
      appendSubAnswerKey(wrap, so);
    });
  }

  // 沒子題時用 q 自身的 answer/explain 當解答(有子題時解答掛在各子題上)。
  if (subs.length === 0) appendAnswerKey(wrap, q);
  return wrap;
}

/** reading 子題的解答(像紅筆訂正)。沿用 .exam-paper-answer 受同一個 toggle 控制。 */
function appendSubAnswerKey(wrap: HTMLElement, sub: Record<string, unknown>): void {
  const subQ: NormQ = {
    id: typeof sub.id === 'string' ? sub.id : '',
    kind: typeof sub.kind === 'string' ? sub.kind : 'mcq',
    prompt: typeof sub.prompt === 'string' ? sub.prompt : '',
    options: Array.isArray(sub.options) ? sub.options.map((o) => String(o)) : undefined,
    answer:
      typeof sub.answer === 'string' || typeof sub.answer === 'number' ? sub.answer : undefined,
    explain: typeof sub.explain === 'string' ? sub.explain : undefined,
  };
  appendAnswerKey(wrap, subQ);
}

// ── 解答(像紅筆訂正,toggle 顯示) ───────────────────────────────────────────

function appendAnswerKey(wrap: HTMLElement, q: NormQ): void {
  const ans = document.createElement('div');
  ans.className = 'exam-paper-answer';
  // 紅筆訂正:克制的墨紅,左側細紅線(像老師批改的紅筆)。
  ans.style.cssText =
    'margin:6px 0 0;color:#b3261e;font-size:14px;white-space:pre-wrap;border-left:3px solid #b3261e;padding-left:8px;';

  const lines: string[] = [];
  const answerText = formatAnswer(q);
  if (answerText) lines.push(`正解:${answerText}`);
  if (q.explain) lines.push(`解析:${q.explain}`);
  if (lines.length === 0) lines.push('(此題為開放/示範題,對照題目與課本作答)');
  ans.textContent = lines.join('\n');

  wrap.appendChild(ans);
}

/** 把標準答案顯示成人看得懂的字串(mcq 把 index 換成選項字母 + 文字)。 */
function formatAnswer(q: NormQ): string {
  if (q.answer === undefined) return '';
  if ((q.kind === 'mcq' || q.kind === 'image-mcq') && typeof q.answer === 'number') {
    const opt = q.options?.[q.answer];
    const letter = String.fromCharCode(65 + q.answer);
    return opt !== undefined ? `(${letter}) ${opt}` : `(${letter})`;
  }
  if (q.kind === 'tf') {
    const a = String(q.answer).toUpperCase();
    return a === 'O' || a === '○' ? '○(對)' : '✕(錯)';
  }
  return String(q.answer);
}

/** 把子項物件描述成一行文字(check / match / fill-grid 子項)。 */
function describeItem(it: unknown): string {
  if (typeof it === 'string') return `• ${it}`;
  if (typeof it === 'object' && it !== null) {
    const o = it as Record<string, unknown>;
    const text =
      typeof o.text === 'string' ? o.text
      : typeof o.name === 'string' ? o.name
      : typeof o.prompt === 'string' ? o.prompt
      : '';
    return `• ${text}`.trim() || '•';
  }
  return `• ${String(it)}`;
}

// ── 列印(沿用 pdf-gen 的 window.open + window.print 模式) ──────────────────

/**
 * 把這張卷組成獨立 HTML,開新視窗 + window.print()。版面語彙沿用
 * printable/pdf-gen.ts(A4、姓名/日期欄、題號、選項、作答框/底線)。
 */
function printPaper(paper: ExamPaperEntry): void {
  const html = buildPrintableHtml(paper);
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}

/** 產生列印用 HTML 字串(可獨立測試)。 */
export function buildPrintableHtml(paper: ExamPaperEntry): string {
  const sections = groupBySection(paper.questions);
  let qNum = 0;
  const sectionsHtml = sections
    .map((section, sIdx) => {
      const qs = section.questions
        .map((q) => {
          qNum += 1;
          return printableQuestion(q, qNum);
        })
        .join('');
      return `<div class="section">
        <div class="section-title">${CJK_NUM[sIdx] ?? sIdx + 1}、${escapeHtml(section.label)}</div>
        ${qs}
      </div>`;
    })
    .join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${escapeHtml(paper.title)}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: "Songti TC", "STSong", "SimSun", "PMingLiU", serif;
    color: #000; line-height: 1.6;
  }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 16px; }
  .title { font-size: 22px; font-weight: bold; text-align: center; }
  .meta { font-size: 12px; margin-top: 8px; display: flex; flex-wrap: wrap; gap: 16px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .section { margin-bottom: 20px; }
  .section-title { font-size: 16px; font-weight: bold; margin: 12px 0 10px; border-left: 4px solid #000; padding-left: 8px; }
  .q { margin-bottom: 16px; page-break-inside: avoid; }
  .prompt { font-size: 14px; margin-bottom: 6px; white-space: pre-wrap; }
  .opts { display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; margin-bottom: 6px; }
  .opt { padding: 2px 6px; }
  .answer-line { font-size: 13px; }
  .answer-line .blank { display: inline-block; min-width: 6em; border-bottom: 1px solid #000; padding: 0 4px; }
  .paren { font-size: 14px; }
  .fill-box { border: 1px solid #000; width: 80mm; height: 18mm; }
  .items { font-size: 13px; white-space: pre-wrap; }
  .passage { background: #f6f4ee; border: 1px solid #ccc; border-radius: 3px; padding: 10px; margin-bottom: 8px; font-size: 13px; white-space: pre-wrap; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${escapeHtml(paper.title)}</div>
  <div class="meta">
    <span>班級:<span class="blank">&nbsp;</span></span>
    <span>姓名:<span class="blank">&nbsp;</span></span>
    <span>日期:<span class="blank">&nbsp;</span></span>
    <span>得分:<span class="blank">&nbsp;</span></span>
  </div>
</div>
${sectionsHtml}
</body>
</html>`;
}

/** 單題的列印 HTML(對應 renderQuestion 的版面)。 */
function printableQuestion(q: NormQ, num: number): string {
  if (q.kind === 'reading') {
    return printableReading(q, num);
  }

  const prompt = `<div class="prompt">${num}. ${escapeHtml(q.prompt)}</div>`;

  let body = '';
  switch (q.kind) {
    case 'mcq':
    case 'image-mcq': {
      const opts = (q.options ?? [])
        .map((o, idx) => `<span class="opt">(${String.fromCharCode(65 + idx)}) ${escapeHtml(o)}</span>`)
        .join('');
      body = `${opts ? `<div class="opts">${opts}</div>` : ''}<div class="paren">答(　　)</div>`;
      break;
    }
    case 'tf':
      body = '<div class="paren">答(　　)</div>';
      break;
    case 'fill':
    case 'word-bank-fill':
    case 'compute':
    case 'inverse':
    case 'zhuyin-word':
      body = '<div class="answer-line">答:<span class="blank">&nbsp;</span></div>';
      break;
    case 'word':
      body = '<div class="fill-box"></div>';
      break;
    case 'check':
    case 'match':
    case 'fill-grid': {
      const items = Array.isArray(q.items)
        ? q.items.map((it) => `<div class="items">${escapeHtml(describeItem(it))}</div>`).join('')
        : '';
      body = `${items}<div class="fill-box"></div>`;
      break;
    }
    default:
      body = '<div class="fill-box"></div>';
  }

  return `<div class="q">${prompt}${body}</div>`;
}

/** reading 題的列印 HTML。 */
function printableReading(q: NormQ, num: number): string {
  const passage = `<div class="passage">${escapeHtml(q.passage ?? q.prompt)}</div>`;
  const subs = Array.isArray(q.items) ? q.items : [];
  if (subs.length === 0) {
    return `<div class="q">${passage}<div class="prompt">${num}. ${escapeHtml(q.prompt)}</div><div class="answer-line">答:<span class="blank">&nbsp;</span></div></div>`;
  }
  const body = subs
    .map((sub, i) => {
      const so = (typeof sub === 'object' && sub !== null ? sub : {}) as Record<string, unknown>;
      const subPrompt =
        typeof so.prompt === 'string' ? so.prompt
        : typeof so.text === 'string' ? so.text
        : String(sub);
      const promptHtml = `<div class="prompt">${num}-${i + 1}. ${escapeHtml(subPrompt)}</div>`;
      if (Array.isArray(so.options) && so.options.length > 0) {
        const opts = so.options
          .map((o, idx) => `<span class="opt">(${String.fromCharCode(65 + idx)}) ${escapeHtml(String(o))}</span>`)
          .join('');
        return `${promptHtml}<div class="opts">${opts}</div><div class="paren">答(　　)</div>`;
      }
      return `${promptHtml}<div class="answer-line">答:<span class="blank">&nbsp;</span></div>`;
    })
    .join('');
  return `<div class="q">${passage}${body}</div>`;
}

// ── small helpers ────────────────────────────────────────────────────────────

/** 一段底線占位(min-width = width)。 */
function blank(width: string): string {
  return `<span class="exam-paper-blank" style="display:inline-block;min-width:${width};border-bottom:1px solid var(--ef-ink);"></span>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
